import { formatDate, formatDateTime, formatTime } from '@comforthub/design-system'
import { api } from './api'

/**
 * Cloud receipt printing: a print app on a phone next to the store's receipt
 * printer polls service-core for jobs and prints them. Contract:
 * service-core "print agent" (GET/POST/DELETE /api/stores/{id}/printer,
 * POST /api/orders/{id}/print, GET /api/orders/{id}/print-jobs).
 *
 * Print agent 1.3.0 adds remote management (service-core V147): the phone's
 * settings (PATCH /printer/settings), commands for it (POST/GET
 * /printer/commands), its log (GET /printer/log) and the install command (GET
 * /printer/install). Every field it adds to the printer is optional here: an
 * older service-core sends none of them, and the dialog hides what it lacks.
 *
 * Alerts per store printer (service-core V166): who hears when the printer
 * stops and works again, by e-mail or field-app push, after how long, and
 * whether only while the store is open (PATCH /printer/alerts). The platform's
 * printer.offline / printer.back_online switches stay the master switch
 * (alertsPlatform, read only).
 */

export interface StorePrinter {
  id: string
  storeId: string
  name?: string | null
  enabled: boolean
  lastSeenAt?: string | null
  /** The print app was heard from in the last 90 s. */
  online: boolean
  printerReachable?: boolean | null
  agentVersion?: string | null
  printerAddress?: string | null
  lastError?: string | null
  lastPrintedAt?: string | null
  createdAt?: string | null
  /** From health alerts. Null when nothing is wrong. */
  problemKind?: PrinterProblemKind | string | null
  problemSince?: string | null
  /** What the printer says it is, "EPSON|TM-T88III|ZY2205AB0001". */
  printerIdentity?: string | null
  /** The phone's address on the shop Wi-Fi. */
  phoneIp?: string | null
  agentSha256?: string | null
  /** The settings version the backoffice asked for, and the one the phone applied. */
  configVersion?: number | null
  configApplied?: number | null
  /** Settings were saved here and the phone hasn't applied them yet. */
  configPending?: boolean | null
  /** What the backoffice asks the phone to use. Missing on a service-core without remote management. */
  settings?: PrinterSettings | null
  latestAgentVersion?: string | null
  updateAvailable?: boolean | null
  lastLogAt?: string | null
  /** The store's alert settings, defaults filled in. Missing on a service-core without them. */
  alerts?: PrinterAlerts | null
  /** The platform switches; false switches that channel off for every store. */
  alertsPlatform?: PrinterAlertsPlatform | null
  /** The store has opening hours ("Only while the store is open" needs them). */
  storeHasHours?: boolean | null
}

export type PrinterAlertRecipients = 'managers' | 'chosen'

export interface PrinterAlerts {
  /** "Receipt printer offline" by e-mail / push to the field app. */
  offlineEmail: boolean
  offlinePush: boolean
  /** "Receipt printer back online" by e-mail / push. */
  backEmail: boolean
  backPush: boolean
  /** How long a problem lasts before the alert, 1–60. A failed print alerts at once. */
  alertAfterMinutes: number
  /** Alert only while the store's opening hours say it is open. */
  onlyWhenOpen: boolean
  /** Owners and managers, or the people in userIds. */
  recipients: PrinterAlertRecipients | string
  userIds: string[]
  /** Up to five more addresses, e-mail only. */
  extraEmails: string[]
}

export interface PrinterAlertsPlatform {
  offlineEmail: boolean
  offlinePush: boolean
  backEmail: boolean
  backPush: boolean
}

/** Only what changed is sent; the rest keeps what it has. */
export type PrinterAlertsPatch = Partial<PrinterAlerts>

export type PrinterProblemKind = 'agent_offline' | 'printer_unreachable' | 'print_failed'

export interface PrinterSettings {
  /** "auto" finds the printer on the Wi-Fi; an IPv4 is a fixed address; null keeps what the phone has. */
  printerHost?: string | null
  printerPort?: number | null
  /** Look for the printer again when it stops answering at its address. */
  autoFind?: boolean | null
  /** The phone installs a new print app by itself. */
  autoUpdate?: boolean | null
}

export interface PrinterSettingsPatch {
  printerHost?: string
  printerPort?: number
  autoFind?: boolean
  autoUpdate?: boolean
}

export type PrinterCommandKind = 'restart' | 'update' | 'find_printer' | 'send_log'
export type PrinterCommandStatus = 'pending' | 'sent' | 'done' | 'failed'

export interface PrinterCommand {
  id: string
  kind: PrinterCommandKind | string
  status: PrinterCommandStatus | string
  /** What the phone answered. Null for send_log: the log itself is under GET /printer/log. */
  result?: string | null
  createdAt?: string | null
  finishedAt?: string | null
}

export interface PrinterLog {
  text: string
  at?: string | null
}

export interface PrinterInstall {
  command: string
  apiBase?: string | null
}

/** POST /printer answers with the printer and its key. The key is shown once. */
export interface ConnectedStorePrinter extends StorePrinter {
  token: string
}

export type PrintJobStatus = 'queued' | 'sent' | 'printed' | 'failed' | 'cancelled'

export interface PrintJob {
  id: string
  /** time_change: service-core reprinted because the pickup time moved after the slip printed. */
  reason: 'auto' | 'manual' | 'test' | 'time_change' | string
  status: PrintJobStatus | string
  attempts?: number
  error?: string | null
  createdAt?: string | null
  finishedAt?: string | null
}

const statusOf = (err: unknown) => (err as { response?: { status?: number } } | null)?.response?.status

const isNotFound = (err: unknown) => statusOf(err) === 404

/** 404/405 on an endpoint: this service-core doesn't have it yet. */
export const isNotSupported = (err: unknown) => statusOf(err) === 404 || statusOf(err) === 405

/** The store's printer, or null when none is connected (404). */
export async function fetchStorePrinter(storeId: string): Promise<StorePrinter | null> {
  try {
    const res = await api.get<StorePrinter>(`/stores/${storeId}/printer`)
    return res.data ?? null
  } catch (err) {
    if (isNotFound(err)) return null
    throw err
  }
}

/** Connects a printer, or gives the connected one a new key (the old key stops working). */
export async function connectStorePrinter(storeId: string): Promise<ConnectedStorePrinter> {
  const res = await api.post<ConnectedStorePrinter>(`/stores/${storeId}/printer`, {})
  return res.data
}

export async function removeStorePrinter(storeId: string): Promise<void> {
  await api.delete(`/stores/${storeId}/printer`)
}

export async function printStoreTestSlip(storeId: string): Promise<void> {
  await api.post(`/stores/${storeId}/printer/test`)
}

/** Saves what the phone should use. The phone applies it when it next checks in (configPending until then). */
export async function updatePrinterSettings(storeId: string, patch: PrinterSettingsPatch): Promise<StorePrinter> {
  const res = await api.patch<StorePrinter>(`/stores/${storeId}/printer/settings`, patch)
  return res.data
}

/** Saves who hears about the printer and when. Sends nothing by itself. */
export async function updatePrinterAlerts(storeId: string, patch: PrinterAlertsPatch): Promise<StorePrinter> {
  const res = await api.patch<StorePrinter>(`/stores/${storeId}/printer/alerts`, patch)
  return res.data
}

/** Asks the phone to do something. If the same kind is already waiting, the server answers with that one. */
export async function sendPrinterCommand(storeId: string, kind: PrinterCommandKind): Promise<PrinterCommand> {
  const res = await api.post<PrinterCommand>(`/stores/${storeId}/printer/commands`, { kind })
  return res.data
}

/** The latest 20 commands, newest first. */
export async function fetchPrinterCommands(storeId: string): Promise<PrinterCommand[]> {
  const res = await api.get<PrinterCommand[]>(`/stores/${storeId}/printer/commands`)
  return Array.isArray(res.data) ? res.data : []
}

/** The last log the phone sent, or null when it hasn't sent one yet (404). */
export async function fetchPrinterLog(storeId: string): Promise<PrinterLog | null> {
  try {
    const res = await api.get<PrinterLog>(`/stores/${storeId}/printer/log`)
    const data = res.data
    return data && typeof data.text === 'string' ? data : null
  } catch (err) {
    if (isNotFound(err)) return null
    throw err
  }
}

/** The command that installs the print app on a new phone, or null when this service-core can't give one. */
export async function fetchPrinterInstall(storeId: string): Promise<PrinterInstall | null> {
  try {
    const res = await api.get<PrinterInstall>(`/stores/${storeId}/printer/install`)
    const data = res.data
    return data && typeof data.command === 'string' && data.command.trim() ? data : null
  } catch (err) {
    if (isNotSupported(err)) return null
    throw err
  }
}

/** The service-core knows remote management: it sends the phone's settings with the printer. */
export const supportsRemoteManagement = (printer: StorePrinter | null | undefined) =>
  printer?.settings != null && typeof printer.settings === 'object'

/** The service-core has alert settings per store printer (V166). */
export const supportsPrinterAlerts = (printer: StorePrinter | null | undefined) =>
  printer?.alerts != null && typeof printer.alerts === 'object'

export const MIN_ALERT_AFTER_MINUTES = 1
export const MAX_ALERT_AFTER_MINUTES = 60
export const MAX_EXTRA_EMAILS = 5

/** Why the alert delay can't be saved, or null when it can. */
export function alertAfterError(value: string): string | null {
  const text = value.trim()
  const minutes = Number(text)
  if (!/^\d+$/.test(text) || minutes < MIN_ALERT_AFTER_MINUTES || minutes > MAX_ALERT_AFTER_MINUTES) {
    return `Use ${MIN_ALERT_AFTER_MINUTES} to ${MAX_ALERT_AFTER_MINUTES} minutes.`
  }
  return null
}

/** The address as service-core keeps it: trimmed and lower-case. */
export const normaliseEmail = (value: string) => value.trim().toLowerCase()

/** Same shape service-core accepts: something@something.something, no spaces. */
export function isEmailAddress(value: string): boolean {
  const text = value.trim()
  return text.length <= 254 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(text)
}

/** A command the phone hasn't answered yet. */
export const isCommandPending = (command: PrinterCommand | undefined | null) =>
  command?.status === 'pending' || command?.status === 'sent'

/** The newest command of that kind; the list is newest first. */
export const latestCommand = (commands: PrinterCommand[] | undefined | null, kind: PrinterCommandKind) =>
  commands?.find((c) => c.kind === kind)

/** "EPSON|TM-T88III|ZY2205AB0001" reads "EPSON TM-T88III · ZY2205AB0001". */
export function formatPrinterIdentity(identity: string | null | undefined): string | null {
  const raw = (identity ?? '').trim()
  if (!raw) return null
  const [maker = '', model = '', ...rest] = raw.split('|').map((p) => p.trim())
  const name = [maker, model].filter(Boolean).join(' ')
  const parts = [name, ...rest].filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

/** 10.0.0.0/8, 172.16.0.0/12 and 192.168.0.0/16, written as four plain numbers. */
export function isPrivateIPv4(value: string): boolean {
  const text = value.trim()
  if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(text)) return false
  const octets = text.split('.').map(Number)
  if (octets.some((o) => o > 255)) return false
  if (text.split('.').some((o) => o.length > 1 && o.startsWith('0'))) return false
  const [a, b] = octets
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168)
}

/** Why the fixed address can't be saved, or null when it can. */
export function printerHostError(value: string): string | null {
  if (!value.trim()) return "Enter the printer's address, such as 192.168.1.54."
  if (!isPrivateIPv4(value)) return "Use the printer's address on the shop Wi-Fi, such as 192.168.1.54."
  return null
}

/** Why the port can't be saved, or null when it can. */
export function printerPortError(value: string): string | null {
  const text = value.trim()
  const port = Number(text)
  if (!/^\d+$/.test(text) || port < 1 || port > 65535) return 'Use a port from 1 to 65535. Most printers use 9100.'
  return null
}

/** "13:12" today, "13 Oct 2026, 13:12" on another day. */
export function sinceLabel(at: string, now: number = Date.now()): string {
  return formatDate(at) === formatDate(now) ? formatTime(at) : formatDateTime(at)
}

/** One plain sentence for a health-alert problem. */
export function problemLine(kind: string, since?: string | null, now: number = Date.now()): string {
  switch (kind) {
    case 'agent_offline':
      return since ? `The phone has not checked in since ${sinceLabel(since, now)}` : 'The phone has not checked in'
    case 'printer_unreachable':
      return 'The phone cannot reach the printer'
    case 'print_failed':
      return 'An order did not print'
    default:
      return 'Something is wrong with the printer'
  }
}

export const COMMAND_LABELS: Record<PrinterCommandKind, string> = {
  find_printer: 'Find printer',
  restart: 'Restart phone app',
  update: 'Update now',
  send_log: 'Show phone log',
}

/** One line for a command's state, such as "Done 13:20: found at 192.168.1.54". */
export function commandLine(command: PrinterCommand, now: number = Date.now()): string {
  const result = command.result?.trim()
  switch (command.status) {
    case 'pending':
      return 'Waiting for the phone…'
    case 'sent':
      return 'The phone is on it…'
    case 'done': {
      const when = command.finishedAt ? ` ${sinceLabel(command.finishedAt, now)}` : ''
      return result ? `Done${when}: ${result}` : `Done${when}`
    }
    case 'failed':
      return `Failed: ${result || 'no reason given'}`
    default:
      return command.status
  }
}

/** Queues the order on its store's printer. 409 when the store has no printer. */
export async function reprintOrder(orderId: string): Promise<{ jobId: string; status: string }> {
  const res = await api.post<{ jobId: string; status: string }>(`/orders/${orderId}/print`)
  return res.data
}

/** The order's print jobs, newest first. */
export async function fetchOrderPrintJobs(orderId: string): Promise<PrintJob[]> {
  const res = await api.get<PrintJob[]>(`/orders/${orderId}/print-jobs`)
  return Array.isArray(res.data) ? res.data : []
}

/** A job the printer hasn't answered for yet. */
export const isPrintJobPending = (job: PrintJob | undefined | null) =>
  job?.status === 'queued' || job?.status === 'sent'

/** One line for the order's latest print job. */
export function printJobLine(job: PrintJob): string {
  const timeChange = job.reason === 'time_change'
  switch (job.status) {
    case 'printed':
      return timeChange
        ? `Reprinted ${formatDateTime(job.finishedAt || job.createdAt)} — pickup time changed`
        : `Printed ${formatDateTime(job.finishedAt || job.createdAt)}`
    case 'queued':
    case 'sent':
      return timeChange ? 'Waiting for the printer — pickup time changed' : 'Waiting for the printer'
    case 'failed':
      return `Failed: ${job.error || 'no reason given'}`
    case 'cancelled':
      return 'Print cancelled'
    default:
      return job.status
  }
}

/** "just now", "5 min ago", or "13 Oct 2026, 09:12" for anything an hour or older. */
export function lastSeenLabel(lastSeenAt: string, now: number = Date.now()): string {
  const at = new Date(lastSeenAt).getTime()
  if (!Number.isFinite(at)) return '—'
  const minutes = Math.floor((now - at) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  return formatDateTime(lastSeenAt)
}

/**
 * The API's absolute address, as the print app on the phone must call it, e.g.
 * https://api-dev.comforthub.ee/api. Same base as lib/api.ts: VITE_API_URL, or
 * this page's own /api when it isn't set.
 */
export function absoluteApiBase(): string {
  const base = String(import.meta.env.VITE_API_URL ?? '/api')
  try {
    return new URL(base, window.location.origin).href.replace(/\/+$/, '')
  } catch {
    return base
  }
}
